export type SearchFacets = {
  groups: string[];
  subjects: string[];
  types: string[];
  themes: string[];
};

export type StaticSearchDocument = {
  id: string;
  pageUrl: string;
  targetUrl: string;
  title: string;
  description: string;
  breadcrumb: string;
  groupName: string;
  subject: string;
  materialType: string;
  theme: string;
  imageUrl: string;
  downloadUrl: string;
  normalized: string;
  kind: "page" | "section" | "download";
  sortRank: number;
};

export type StaticSearchIndex = {
  schemaVersion: 1;
  qualityVersion: 5;
  generatedAt: string;
  sourceCount: number;
  documentCount: number;
  facets: SearchFacets;
  documents: StaticSearchDocument[];
};

export type SearchInput = {
  query: string;
  group?: string;
  subject?: string;
  materialType?: string;
  theme?: string;
  limit?: number;
};

export type StaticSearchResult = Omit<StaticSearchDocument, "normalized"> & {
  score: number;
};

const SYNONYMS: string[][] = [
  ["kleuter", "kleuters", "groep 1/2", "groep 1 2"],
  ["werkblad", "werkbladen", "oefenblad", "oefening"],
  // "Rekenen" is veel breder dan "sommen" en is daarom bewust geen
  // wederzijds synoniem. Anders zou zoeken op sommen vrijwel elk rekenblad tonen.
  ["sommen", "rekensommen"],
  ["plus", "optellen", "erbij"],
  ["min", "aftrekken", "eraf"],
  ["klokkijken", "klok", "tijd"],
  ["bussom", "bussommen", "bus sommen"],
  ["sinterklaas", "sint", "sinterklas"],
  ["kleurplaat", "kleuren", "kleurplaten"],
  ["maker", "generator", "maken"],
  ["m3", "midden groep 3"],
  ["e3", "eind groep 3"],
  ["b3", "begin groep 3"],
];

export function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&amp;/gi, " en ")
    .replace(/[^a-zA-Z0-9/]+/g, " ")
    .trim()
    .toLowerCase();
}

function baseTerms(query: string): string[] {
  return normalizeText(query)
    .split(" ")
    .filter((term) => term.length > 1 || /^\d$/.test(term))
    .slice(0, 12);
}

function alternativesFor(term: string, normalizedQuery: string): string[] {
  const matchingGroup = SYNONYMS.find((group) =>
    group.some((entry) => {
      const normalizedEntry = normalizeText(entry);
      return normalizedEntry === term || normalizedQuery.includes(normalizedEntry);
    })
  );
  if (!matchingGroup) return [term];
  const alternatives = new Set<string>([term]);
  for (const entry of matchingGroup) {
    const normalizedEntry = normalizeText(entry);
    if (normalizedEntry) alternatives.add(normalizedEntry);
  }
  return [...alternatives];
}

function levenshtein(left: string, right: string): number {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;
  if (Math.abs(left.length - right.length) > 2) return 3;

  const previous = Array.from(
    { length: right.length + 1 },
    (_, index) => index
  );
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    let diagonal = previous[0];
    previous[0] = leftIndex;
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const saved = previous[rightIndex];
      previous[rightIndex] = Math.min(
        previous[rightIndex] + 1,
        previous[rightIndex - 1] + 1,
        diagonal +
          (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1)
      );
      diagonal = saved;
    }
  }
  return previous[right.length];
}

function wordMatches(term: string, words: string[], allText: string): boolean {
  if (allText.includes(term)) return true;
  if (term.length >= 5) {
    const stem = term.slice(0, Math.max(4, term.length - 2));
    if (allText.includes(stem)) return true;
  }
  if (term.length < 4) return false;
  const maximumDistance = term.length >= 8 ? 2 : 1;
  return words.some(
    (word) =>
      word.length >= 3 && levenshtein(term, word) <= maximumDistance
  );
}

function matchesTermGroup(
  alternatives: string[],
  titleWords: string[],
  allText: string
): boolean {
  return alternatives.some((term) => wordMatches(term, titleWords, allText));
}

function scoreDocument(
  document: StaticSearchDocument,
  query: string,
  termGroups: string[][]
): number {
  const title = normalizeText(document.title);
  const description = normalizeText(document.description);
  const allText = document.normalized;
  const phrase = normalizeText(query);
  const titleWords = title.split(" ");
  // De bronpagina hoort altijd vóór een rechtstreeks downloadresultaat te
  // staan. Bepaal dit ook tijdens het zoeken, zodat een oudere gecachte index
  // niet alsnog downloads boven de pagina kan plaatsen.
  let score = document.kind === "section" ? 100 : document.kind === "page" ? 90 : 30;

  if (title === phrase) score += 240;
  else if (title.startsWith(phrase)) score += 150;
  else if (title.includes(phrase)) score += 110;

  if (
    termGroups.every((group) =>
      matchesTermGroup(group, titleWords, allText)
    )
  ) {
    score += 45;
  }

  for (const group of termGroups) {
    let best = 0;
    for (const term of group) {
      if (titleWords.includes(term)) best = Math.max(best, 32);
      else if (
        titleWords.some(
          (word) => word.startsWith(term) || term.startsWith(word)
        )
      ) {
        best = Math.max(best, 20);
      } else if (
        term.length >= 4 &&
        titleWords.some(
          (word) =>
            levenshtein(term, word) <= (term.length >= 8 ? 2 : 1)
        )
      ) {
        best = Math.max(best, 16);
      }
      if (description.includes(term)) best = Math.max(best, 8);
      if (allText.includes(term)) best = Math.max(best, 4);
    }
    score += best;
  }

  return score;
}

function matchesFilters(
  document: StaticSearchDocument,
  input: SearchInput
): boolean {
  return (
    (!input.group || document.groupName === input.group) &&
    (!input.subject || document.subject === input.subject) &&
    (!input.materialType || document.materialType === input.materialType) &&
    (!input.theme || document.theme === input.theme)
  );
}

export function searchStaticIndex(
  index: StaticSearchIndex,
  input: SearchInput
): { results: StaticSearchResult[]; total: number } {
  const query = input.query.trim();
  const normalizedQuery = normalizeText(query);
  const terms = baseTerms(query);
  if (!normalizedQuery || terms.length === 0) {
    return { results: [], total: 0 };
  }

  const termGroups = terms.map((term) =>
    alternativesFor(term, normalizedQuery)
  );
  const scored: Array<StaticSearchDocument & { score: number }> = [];

  for (const document of index.documents) {
    if (!matchesFilters(document, input)) continue;
    const titleWords = normalizeText(document.title).split(" ");
    if (
      !termGroups.every((group) =>
        matchesTermGroup(group, titleWords, document.normalized)
      )
    ) {
      continue;
    }
    scored.push({
      ...document,
      score: scoreDocument(document, query, termGroups),
    });
  }

  scored.sort(
    (left, right) =>
      right.score - left.score ||
      left.title.localeCompare(right.title, "nl", { numeric: true })
  );

  const deduplicated = new Map<string, StaticSearchResult>();
  for (const document of scored) {
    const key = `${normalizeText(document.title)}|${document.pageUrl}`;
    if (deduplicated.has(key)) continue;
    const { normalized: _normalized, ...result } = document;
    void _normalized;
    deduplicated.set(key, result);
  }

  const allResults = [...deduplicated.values()];
  const limit = Math.min(Math.max(input.limit ?? 24, 1), 50);
  return {
    results: allResults.slice(0, limit),
    total: allResults.length,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasString(record: Record<string, unknown>, key: string): boolean {
  return typeof record[key] === "string";
}

function isDocument(value: unknown): value is StaticSearchDocument {
  if (!isRecord(value)) return false;
  const stringFields = [
    "id",
    "pageUrl",
    "targetUrl",
    "title",
    "description",
    "breadcrumb",
    "groupName",
    "subject",
    "materialType",
    "theme",
    "imageUrl",
    "downloadUrl",
    "normalized",
    "kind",
  ];
  if (!stringFields.every((field) => hasString(value, field))) return false;
  if (value.kind !== "page" && value.kind !== "section" && value.kind !== "download") return false;
  return typeof value.sortRank === "number" && Number.isFinite(value.sortRank);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

export function isStaticSearchIndex(value: unknown): value is StaticSearchIndex {
  if (!isRecord(value)) return false;
  if (value.schemaVersion !== 1 || value.qualityVersion !== 5) return false;
  if (
    typeof value.generatedAt !== "string" ||
    typeof value.sourceCount !== "number" ||
    typeof value.documentCount !== "number" ||
    !Array.isArray(value.documents) ||
    !value.documents.every(isDocument) ||
    !isRecord(value.facets)
  ) {
    return false;
  }
  return (
    isStringArray(value.facets.groups) &&
    isStringArray(value.facets.subjects) &&
    isStringArray(value.facets.types) &&
    isStringArray(value.facets.themes) &&
    value.documentCount === value.documents.length
  );
}
